// The main path of each part of a model drawn on the canvas, for the "Hierarchic main path" layout to line it up along:
// from the start most items begin at, along the connection most items take, as the Actions graph's main flow is read
// (see analysis/main-path.ts). Roles, systems and notes are beside the work rather than on it, so aren't followed
import { IEdge, IGraph, INode } from '@yfiles/yfiles';
import { mainPaths } from '../analysis/main-path';
import { EDGE_TYPE, getInsightsWithoutCreate, Linqset, LinqsetEdge, LinqsetNode, NODE_SUPERTYPE } from '../common/linqset-model';

// the nodes and edges items flow along. Information only used elsewhere carries none, so isn't followed
const FLOW_NODES = [NODE_SUPERTYPE.ACTION, NODE_SUPERTYPE.INFORMATION, NODE_SUPERTYPE.OUTCOMES];
const FLOW_EDGES = [EDGE_TYPE.CREATE, EDGE_TYPE.SUPPORT, EDGE_TYPE.JOINS, EDGE_TYPE.REALISE];

export const modelMainPaths = (graph: IGraph, linqset?: Linqset) => {
    const nodeOf = (n: INode): LinqsetNode | undefined => n.tag?.node;
    const insights = (n: INode) => {
        const node = nodeOf(n);
        return node && linqset ? getInsightsWithoutCreate(node, linqset) : undefined;
    };
    const itemsAt = (n: INode) => insights(n)?.numberProcessed || 0;
    const flowing = (e: IEdge) => FLOW_EDGES.includes((e.tag?.edge as LinqsetEdge | undefined)?.type as EDGE_TYPE);
    const nodes = graph.nodes.filter(n => FLOW_NODES.includes(nodeOf(n)?.superType as NODE_SUPERTYPE)).toArray();
    return mainPaths<INode, IEdge>(nodes, itemsAt,
        n => graph.outEdgesAt(n).filter(flowing).map(e => {
            // the share of the node's items sent along it; between connections carrying as many, the busier step it reaches
            const share = (e.tag.edge as LinqsetEdge).outputPercentage ?? 100;
            return { to: e.targetNode, items: itemsAt(n) * share / 100, share: itemsAt(e.targetNode), via: e };
        }).toArray(),
        n => graph.inEdgesAt(n).filter(flowing).map(e => e.sourceNode).toArray(),
        n => !!insights(n)?.isInput);
};
